import * as React from 'react'
import {graphql} from 'gatsby'
import {getImage} from 'gatsby-plugin-image'
import {withPrismicPreview} from 'gatsby-plugin-prismic-previews'
import {useTheme} from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import CardContent from '@mui/material/CardContent'

import RenderRichText from 'src/utils/renderRichText'
import PersonCard from 'src/components/molecules/PersonCard'
import TopBorderCard from 'src/components/molecules/TopBorderCard'
import useSlice from 'src/hooks/useSlice'
import About from 'src/components/templates/AboutPage'

import type {
  AboutDocument,
  AboutDocumentDataBodyJourneySlice,
  AboutDocumentDataBodyPeopleSlice,
} from '../../types.generated'

interface Props {
  data: {
    prismicAbout: AboutDocument
  }
}

const AboutPage = ({data}: Props) => {
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('md'))
  const hero = useSlice(data.prismicAbout, 'hero')
  const journey = useSlice(
    data.prismicAbout,
    'journey',
  ) as AboutDocumentDataBodyJourneySlice
  const team = useSlice(
    data.prismicAbout,
    'people',
  ) as AboutDocumentDataBodyPeopleSlice
  return (
    <About
      heroContent={() => (
        <>
          <RenderRichText
            field={hero.primary.hero_title}
            components={{
              heading1: ({children}) => (
                <Typography variant="h3" component="h1" gutterBottom>
                  {children}
                </Typography>
              ),
            }}
          />
          <Box sx={{mb: 4}}>
            <RenderRichText field={hero.primary.hero_text} />
          </Box>
        </>
      )}
      journeyContent={() => (
        <>
          {journey.items.sort().map(({title, body_text}, index: number) => (
            <Box
              sx={{
                mt: index === 0 ? 0 : 2,
                width: {md: '47%', xs: '75%'},
                ml: index % 2 ? 'auto' : 0,
                position: 'relative',
              }}
              key={`${title}-${index}`}
            >
              <TopBorderCard>
                <CardContent sx={{p: {xs: 2, md: 3}}}>
                  <RenderRichText field={title} />
                  <RenderRichText field={body_text} />
                </CardContent>
              </TopBorderCard>
            </Box>
          ))}
        </>
      )}
      leadershipContent={() => (
        <>
          <RenderRichText field={team.primary.title} />
          <RenderRichText
            field={team.primary.intro_text}
            components={{
              paragraph: ({children}) => (
                <Typography
                  variant="body1"
                  component="p"
                  gutterBottom
                  sx={{mb: 6, fontWeight: 'normal'}}
                >
                  {children}
                </Typography>
              ),
            }}
          />
          <Grid container spacing={6}>
            {team.items.map(({name, title, avatar, body_text}, i: number) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <PersonCard
                  name={name}
                  title={title}
                  body_text={body_text}
                  image={getImage(avatar)}
                  height={matches ? 250 : undefined}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    />
  )
}

export const query = graphql`
  {
    prismicAbout {
      _previewable
      data {
        body {
          ... on PrismicSliceType {
            slice_type
          }
          ... on PrismicAboutDataBodyHero {
            primary {
              hero_text {
                richText
              }
              hero_title {
                richText
              }
            }
          }
          ... on PrismicAboutDataBodyJourney {
            primary {
              title {
                richText
              }
            }
            items {
              body_text {
                richText
              }
              title {
                richText
              }
            }
          }
          ... on PrismicAboutDataBodyPeople {
            items {
              title {
                richText
              }
              name {
                richText
              }
              body_text {
                richText
              }
              avatar {
                gatsbyImageData
              }
            }
            primary {
              title {
                richText
              }
              intro_text {
                richText
              }
            }
          }
        }
      }
    }
  }
`

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default withPrismicPreview(AboutPage)
